import {Assert, Clone, DeepGet, DeepSet, E, Timer, TimerContext} from "js-vextensions";
import lo from "lodash";
import {GenerateUUID} from "mobx-graphlink";
import moment from "moment";
import type {KeyboardEventHandler, PointerEventHandler} from "react";
import {AssertNotify, CreateGeneralAudioProcessor, InitAudioNodes, LogWarning, RunInAction, VMediaRecorder} from "web-vcore";
import {EngineConfig} from "../Server/DB/engineConfigs/@EngineConfig.js";
import {LaunchType} from "../Server/DB/engineConfigs/@EngineConfig/@EC_ModeSwitcher.js";
import {Setting} from "../Server/DB/engineConfigs/@EngineConfig/@Shared/Setting.js";
import {ScreenState, TriggerSet} from "../Server/DB/engineConfigs/@TriggerSet.js";
import {TriggerInfo, TriggerSet_Activator} from "../Server/DB/engineConfigs/@TriggerSet_Activator.js";
import {GetLights_WithUserTag} from "../Server/DB/lights.js";
import {LightType} from "../Server/DB/lights/@Light.js";
import {GetScripts_WithUserTag} from "../Server/DB/scripts.js";
import {GetSettingNameFromGetter, SessionCoreData, SessionCoreData_propsWithSpecialRestore} from "../Server/DB/sessions/@Session.js";
import {GetSounds_WithUserTag} from "../Server/DB/sounds.js";
import {MeID} from "../Server/DB/users.js";
import {store} from "../Store/index.js";
import {GroupX_AddSessionLogEntry, LogOptions_ForGroupX, SessionLog} from "../UI/Tools/@Shared/BetweenSessionTypes/SessionLog.js";
import {LogEntry, LogType} from "../UI/Tools/@Shared/LogEntry.js";
import {InAndroid, nativeBridge} from "../Utils/Bridge/Bridge_Native.js";
import {PlaySound_StopAll} from "../Utils/Bridge/Bridge_Native/MediaPlayer.js";
import {ClearMotionAveragesBuffer as ResetPhoneMotionAveragesBuffer} from "../Utils/Bridge/Bridge_Native/PhoneSensors.js";
import {remoteUserProcessesBridge} from "../Utils/Bridge/Bridge_RemoteUserProcesses.js";
import {GetOwnInstanceInfo} from "../Utils/Bridge/SiteInstanceBridgeManager.js";
import {PluginGeneral} from "../Utils/Capacitor/GeneralPlugin.js";
import {LightPlayer} from "../Utils/EffectPlayers/LightPlayer.js";
import {ScriptPlayer} from "../Utils/EffectPlayers/ScriptPlayer.js";
import {SoundPlayer} from "../Utils/EffectPlayers/SoundPlayer.js";
import {WeightedRandom} from "../Utils/General/General.js";
import {DisableSleepBlockers, EnableSleepBlockers_Repeating, SleepBlockerOptions} from "../Utils/General/SleepBlockers.js";
import {NarrateText_StopAll} from "../Utils/Services/TTS.js";
import {Vec2} from "../Utils/ThreeJS/VectorStructs.js";
import {EngineSessionComp} from "./LiveSession/Components/EngineSessionComp.js";
import {SessionEvent} from "./LiveSession/SessionEvent.js";
import {NotifyLiveSessionLogChanged, engineEndedSessions} from "./LiveSessionManagement.js";
import {ApplyVariantOverrides} from "./LiveSession/Components/VariantsComp/VariantUtils.js";
import {AlarmsComp} from "./LiveSession/Components/AlarmsComp.js";
import {Phase} from "../Server/DB/@Shared/Phase.js";

type LiveSession_Client = import("./LiveSession_Client.js").LiveSession_Client;
type LiveSession_Local = import("./LiveSession_Local.js").LiveSession_Local;
//type LiveSession_CreateComponents = import("./LiveSession/ComponentRegistry").LiveSession_CreateComponents;

// classes
// ==========

export type MicLoudnessListener = (notifyLoudness: number, actualLoudness: number)=>any;
export type ScreenStateChangeListener = (screenState: ScreenState)=>any;
export type PhaseEnterListener = (newPhase: Phase)=>any;
export type PhoneMotionDataListener = (motionAveragesOfChunksFromLast10s: number[])=>any;
export type LongInhaleListener = (timeOfSignalCompleted: number)=>any;

/*export type TriggerActionFunc = (triggerInfo: TriggerInfo)=>any;
export class TriggerAction {
	func: (triggerInfo: TriggerInfo)=>any;
}*/
export type TriggerAction = (triggerInfo: TriggerInfo)=>any;
export class TriggerPackage {
	constructor(initialData: Partial<TriggerPackage>);
	constructor(name: string, condition: TriggerSet|n, comp: EngineSessionComp, otherInitialData: Partial<TriggerPackage>, actionIfLocal: TriggerAction, actionIfRemote?: TriggerAction);
	constructor(...args) {
		if (args.length == 1) {
			const [initialData] = args as [Partial<TriggerPackage>];
			Object.assign(this, initialData);
		} else {
			const [name, condition, comp, otherInitialData, actionIfLocal, actionIfRemote] = args as [string, TriggerSet, boolean, Partial<TriggerPackage>, TriggerAction, TriggerAction|n];
			Object.assign(this, E(
				{name, condition, comp, actionIfLocal, actionIfRemote},
				otherInitialData
			));
		}
	}

	name: string;
	condition: TriggerSet|n;
	actionIfLocal: TriggerAction;
	actionIfRemote: TriggerAction;
	comp: EngineSessionComp;

	// attached later by FBASession
	activator: TriggerSet_Activator|n;
}

export class BroadcastOptions {
	constructor(data?: Partial<BroadcastOptions>) {
		Object.assign(this, data);
	}
	broadcastToDisabledComps = false;
	additionalCompFilter: (comp: EngineSessionComp<any>)=>boolean|n; // if set, only broadcast to components that pass this filter
}

export abstract class LiveSession {
	constructor(config: EngineConfig, hostConfig: EngineConfig|n, launchType: LaunchType, variantName: string|n) {
		this.coreData.id_live = GenerateUUID();

		// config handling
		this.config_orig = config;
		const config_final = Clone(this.config_orig) as EngineConfig;
		const variant = this.config_orig.variants.variants.find(a=>a.name == variantName);
		if (variantName != null) {
			ApplyVariantOverrides(variantName, this.config_orig, config_final, this.config_fieldOverrides_origValues);
		}
		this.config = config_final;
		this.hostConfig = hostConfig || this.config;

		RunInAction("LiveSession.constructor", ()=>{
			this.coreData.launchType = launchType;
			this.coreData.variantName = variant?.name; // only store variant-name if we actually found the specified variant in the config
		});
		Assert(MeID() != null, "User must be signed-in to run a session.");
		this.userID = MeID()!;
		this.CreateComponents();
	}

	private config_orig: EngineConfig; // we don't actually use this anywhere atm, but still store it (eg. so raw config can be inspected in dev-tools)
	config_fieldOverrides_origValues: {[fieldPath: string]: any} = {};
	config: EngineConfig;
	hostConfig: EngineConfig;
	get c() { return this.config; } // alias
	//get hc() { return this.IsRemote() ? this.hostConfig : this.config; } // alias
	get hc() { return this.hostConfig; } // alias

	userID: string; // user must be signed-in to run a session; store this as non-null for easier type-checking (complicates code too much otherwise)

	timerContext = new TimerContext();

	running: boolean;
	localOffsetFromUTC: number;
	folderName: string;
	endTime: number;

	// listeners (only the first listener in each of these arrays is actually "attached" natively; the rest are called by the first listener)
	keyDownListeners: KeyboardEventHandler<any>[];
	keyUpListeners: KeyboardEventHandler<any>[];
	/*mouseDownListeners: MouseEventHandler<any>[];
	mouseUpListeners: MouseEventHandler<any>[];
	mouseMoveListeners: MouseEventHandler<any>[];*/
	mouseDownListeners: PointerEventHandler<any>[];
	mouseUpListeners: PointerEventHandler<any>[];
	mouseMoveListeners: PointerEventHandler<any>[];
	micLoudnessListeners: MicLoudnessListener[];
	screenStateChangeListeners: ScreenStateChangeListener[];
	/** Like the other listener-arrays, only the first listener is actually "registered" (called by AlarmsComp.SetPhase); the rest are called by it. */
	phaseEnterListeners: PhaseEnterListener[];
	phoneMotionDataListeners: PhoneMotionDataListener[];
	longInhaleListeners: LongInhaleListener[];

	// microphone system
	micRecorder = new VMediaRecorder();
	micRecorder_audioContext: AudioContext;

	// players
	//textSpeaker = new TextSpeaker(); // this is for web-tts api (on android, this is not used)
	effectSoundPlayer = new SoundPlayer();
	startupScriptPlayer = new ScriptPlayer();
	resetScreenLightPlayer = new LightPlayer();

	heartbeatTimer: Timer|n;
	sleepBlockersTimer: Timer|n;

	// components
	components = [] as EngineSessionComp<any>[];
	protected AddComponent(comp: EngineSessionComp<any>) {
		this.components.push(comp);
	}
	// components (new approach)
	CreateComponents() {
		// eslint-disable-next-line
		const {LiveSession_CreateComponents} = require("./LiveSession/ComponentRegistry") as typeof import("./LiveSession/ComponentRegistry.js"); // late-require, avoiding circular importing

		LiveSession_CreateComponents.call(this);
	}

	//GetComponent<CompType extends FBASessionComponent>(compType: new(..._)=>CompType): CompType {
	/** Variant of Comp(), which only requests the component's class-name, rather than the class itself. (can help for eg. javascript import-cycle cases) */
	Comp_ByString<CompType extends EngineSessionComp>(compTypeName: string): CompType {
		return this.components.find(a=>a.constructor.name == compTypeName) as CompType;
	}
	Comp<CompType extends EngineSessionComp>(compType: new(..._)=>CompType): CompType {
		return this.components.find(a=>a instanceof compType) as CompType;
	}
	Comps<CompType extends EngineSessionComp>(compType: new(..._)=>CompType): CompType[] {
		return this.components.filter(a=>a instanceof compType) as CompType[];
	}
	Comps_Enabled<CompType extends EngineSessionComp>(compType: new(..._)=>CompType): CompType[] {
		return this.components.filter(a=>a instanceof compType && a.behaviorEnabled) as CompType[];
	}
	TryCallOnComp(compTypeName: string, methodName: string, ...args: any[]) {
		const comp = this.components.find(a=>a.constructor.name == compTypeName);
		return comp?.[methodName](...args);
	}
	/*Broadcast<T>(funcCaller: (comp: EngineSessionComp<any>)=>any);
	Broadcast<T>(options: BroadcastOptions, funcCaller: (comp: EngineSessionComp<any>)=>any);
	Broadcast(...args) {
		let options: BroadcastOptions, funcCaller: (comp: EngineSessionComp<any>)=>any);
		if (IsFunction(args[0])) [funcCaller] = args;
		else [options, funcCaller] = args;
		options = E(new BroadcastOptions(), options!);

		this.components.forEach(comp=>{
			if (!comp.behaviorEnabled && !options.broadcastToDisabledComps && !func["callEvenWhenCompDisabled"]) return;
			funcCaller(comp);
		});
	}*/
	/*Broadcast<T>(funcNameOrGetter: string | ((comp: EngineSessionComp<any>)=>T), ...funcArgs: any[]);
	Broadcast<T>(options: BroadcastOptions, funcNameOrGetter: string | ((comp: EngineSessionComp<any>)=>T), ...funcArgs: any[]);
	Broadcast(...args) {
		let options: BroadcastOptions, funcNameOrGetter: string | ((comp: EngineSessionComp<any>)=>any), funcArgs: any[];
		if (IsString(args[0]) || IsFunction(args[0])) [funcNameOrGetter, ...funcArgs] = args;
		else [options, funcNameOrGetter, funcArgs] = args;
		options = E(new BroadcastOptions(), options!);

		const funcName = IsString(funcNameOrGetter) ? funcNameOrGetter : ConvertPathGetterFuncToPropChain(funcNameOrGetter).Last();
		this.components.forEach(comp=>{
			const func = comp[funcName] as Function;
			if (func == null) return;
			if (!comp.behaviorEnabled && !options.broadcastToDisabledComps && !func["callEvenWhenCompDisabled"]) return;
			func.call(comp, ...funcArgs);
		});
	}*/
	Broadcast<T extends(...args: any[])=>any>(options: Partial<BroadcastOptions>, funcGetter: (comp: EngineSessionComp<any>)=>T, ...funcArgs: Parameters<T>) {
		const opt = new BroadcastOptions(options);
		this.components.forEach(comp=>{
			const func = funcGetter(comp) as Function; // eslint-disable-line @typescript-eslint/no-unsafe-function-type
			if (func == null) return;
			if (!comp.behaviorEnabled && !opt.broadcastToDisabledComps && !func["callEvenWhenCompDisabled"]) return;
			if (opt.additionalCompFilter && !opt.additionalCompFilter(comp)) return;
			func.call(comp, ...funcArgs);
		});
	}

	IsLocal(): this is LiveSession_Local {
		//return this instanceof LiveSession_Local;
		return this.constructor.name == "LiveSession_Local"; // use constructor-name, so we don't reference the class itself (since causes circular imports between the base-class and subclasses)
	}
	IsClient(): this is LiveSession_Client {
		//return this instanceof LiveSession_Client;
		return this.constructor.name == "LiveSession_Client";
	}
	get AsLocal(): LiveSession_Local|n { return this.IsLocal() ? this : null; }
	get AsClient(): LiveSession_Client|n { return this.IsClient() ? this : null; }

	// abstract methods
	//abstract Start();
	/*abstract CreateBridge();
	abstract DestroyBridge();*/
	//abstract CreateListeners();
	//abstract CreateTimers();
	//abstract Stop();

	coreData_fromRecovery = new SessionCoreData();
	coreData = new SessionCoreData();
	LoadRecoveryInfo(recoveryInfo: SessionCoreData) {
		// store the loaded recovery-info in a separate field (eg. for dev-tool inspection); the current recovery-info should be re-populated organically as the restore process is performed (eg. to avoid two copies of each restored session-event)
		this.coreData_fromRecovery = recoveryInfo;

		// first, restore the "simple props" that require no special processing
		RunInAction("LiveSession.LoadRecoveryInfo", ()=>{
			Object.assign(this.coreData, recoveryInfo.ExcludeKeys(...SessionCoreData_propsWithSpecialRestore));
		});

		// second, restore the events array
		if (this.AsLocal) {
			const mergedEventsArray = [
				...recoveryInfo.events,
				new SessionEvent({type: "General.SessionRecovered"}),
				...this.AsLocal.EventsCopy,
			];
			this.AsLocal["events"].Clear();
			RunInAction("LiveSession_Local.LoadRecoveryInfo.clearEventsBeforeMerging", ()=>this.coreData.events.Clear());
			for (const event of mergedEventsArray) {
				this.AsLocal.AddEvent(event);
			}
		}

		// third, restore other state -- within the calls to `FBASession.Start(recoveryInfo)` and `MyComp.OnStart(recoveryInfo)`.
	}
	GetSettingValue<T>(settingGetter: (config: EngineConfig)=>Setting<T>) {
		const setting = settingGetter(this.c);
		const settingName = GetSettingNameFromGetter(settingGetter);
		if (!Object.keys(this.coreData.settingValuesSelected).includes(settingName)) {
			const settingValues_enabled = lo.shuffle(setting.values.filter(a=>!a.disabled)); // shuffling isnt really necessary, but do it just in case
			AssertNotify(settingValues_enabled.length > 0, `No values available for setting: ${settingName} (may cause crashes if left undefined in config)`);

			const selectedSettingValue = WeightedRandom(settingValues_enabled, a=>a.weight ?? 1);
			this.coreData.settingValuesSelected[settingName] = selectedSettingValue!;
		}
		// use defensive "?.value", in case user left it undefined; bad state, but warning-notification (see above) sufficient / better than always crashing (since session may already be active, and user asleep)
		return this.coreData.settingValuesSelected[settingName]?.value;
	}

	Start(recoveryInfo: SessionCoreData|n, calledFromGlobalFunc: boolean) {
		Assert(calledFromGlobalFunc, "Currently, LiveSession.Start() must be called from the SetAndStartLiveSession() function.");
		Assert(!this.running);
		this.running = true;
		// if we're restoring from recovery-info, the crashed session's start-time is used as our own session start-time (eg. so that the restored-event timestamps make sense)
		const startMoment = recoveryInfo?.startTime ? moment(recoveryInfo.startTime) : moment();
		if (this.coreData.startTime == null) { // if restored from crash, no need to set this again
			RunInAction("LiveSession.Start", ()=>this.coreData.startTime = startMoment.valueOf());
		}
		this.localOffsetFromUTC = startMoment.utcOffset();
		this.folderName = startMoment.format("YYYY-MM-DD-HH-mm-ss");
		this.Log(`Starting ${this.IsLocal() ? "local" : "remote"} FBA session`, LogType.Event_Large);

		this.Broadcast({}, a=>a.OnStart_Early);
		//this.CreateBridge();
		/*await*/ this.CreateListeners();
		this.RegisterTriggerPackages();
		this.SetUpTriggerActivators();
		if (InAndroid(0)) this.InitAndroid();

		if (this.IsLocal()) {
			this.CreateTimers();
			this.initialDelayTimer.Start();
			if (this.c.general.autoEnd_enabled) this.autoEndDelayTimer.Start();
		} else if (this.IsClient()) {
			this.CreateTimers();
			this.statusReportTimer.Start();
		}

		// todo: maybe change heartbeat system to start at time of JS startup, or at time of first Root comp render (and then just have each heartbeat send whether a session is active at that point)
		this.heartbeatTimer = new Timer(10000, ()=>nativeBridge.Call("Heartbeat")).Start();
		// at start of session, also send a set of 6 artificial heartbeats (one minute's worth); this ensures the crash-checker is satisifed at the start, despite real heartbeats not having been amassed enough to satisfy its requirements
		for (let i = 0; i < 6; i++) {
			nativeBridge.Call("Heartbeat");
		}

		// on initial sleep-blocking, use all sleep-blockers; for repeating, only use the anti-web-view-optimizations sleep-blocker
		if (this.c.general.sleepBlockers_enabled) {
			this.sleepBlockersTimer = EnableSleepBlockers_Repeating(new SleepBlockerOptions(true), new SleepBlockerOptions(false, {antiWebViewOptimizations: true}), 30000);
		}
		// todo: make this run all matching scripts
		this.startupScriptPlayer.script = GetScripts_WithUserTag(this.c.general.startupScriptTag).Random();
		this.startupScriptPlayer.Play(1);
		this.resetScreenLightPlayer.light = GetLights_WithUserTag(this.c.general.resetScreenLightTag).filter(a=>a.type == LightType.Screen).Random();

		this.Broadcast({}, a=>a.OnStart, recoveryInfo);
		this.Broadcast({}, a=>a.MaybeUpdateActiveState, {sessionStarting: true}); // "activation" happens after "start"

		if (store.main.engine.config.allowClientConnections) {
			remoteUserProcessesBridge.Call("HostSessionStarted_AllowingClientConnections", GetOwnInstanceInfo());
		}
	}
	Stop(saveLocally: boolean, saveOnline: boolean, calledFromGlobalFunc: boolean) {
		Assert(calledFromGlobalFunc, "Currently, FBASession.Stop() must be called from the ClearAndStopLiveFBASession() function.");
		Assert(this.running);
		this.running = false;
		this.endTime = Date.now();
		this.Log(`Stopping ${this.IsLocal() ? "local" : "remote"} FBA session`, LogType.Event_Large);

		//this.DestroyBridge();
		this.timerContext.Reset(); // stop all timers
		this.RemoveListeners();
		// stop Android side
		if (InAndroid(0)) PluginGeneral.StopSessionHelpers();
		//this.textSpeaker.Stop();
		NarrateText_StopAll();
		this.effectSoundPlayer.Stop();
		this.startupScriptPlayer.Stop();
		// temp; just stop all MediaPlayer instances (needed for reliable stop of all audio-plays, till we have instance-based stopping)
		PlaySound_StopAll();

		if (this.heartbeatTimer) {
			this.heartbeatTimer.Stop();
			this.heartbeatTimer = null;
		}
		if (this.sleepBlockersTimer) {
			this.sleepBlockersTimer.Stop();
			this.sleepBlockersTimer = null;
			DisableSleepBlockers();
		}

		this.Broadcast({}, a=>a.MaybeUpdateActiveState, {sessionStopping: true}); // "deactivation" happens before "stop"
		this.Broadcast({}, a=>a.OnStop, saveLocally);

		// do at end (of synchronous portion), since some things still want to access to liveSession, while shutting down (eg. SonicBomb.StopShake(), sending to remote)
		engineEndedSessions.push(this);

		if (this.IsLocal()) {
			// note: this call is async, but we don't await it; we don't want to block the session-end process on it
			this.FinalizeStoredData(saveLocally, saveOnline);
		}
	}

	// pointer-move window is not used if config is invalid (ie. would have no meaningful behavior anyway); behavior then becomes the base "any mouse-move event causes a trigger"
	get UsePointerMoveWindow() { return (this.c.general.mouseMove_compareWindow ?? 0) > 0 && (this.c.general.mouseMove_minDistToTrigger ?? 0) > 0; }

	recentPointerPositions = new Map<number, Vec2>();
	currentPointerInput_processedPositions = new Set<string>();
	// Note: The "root listeners" created here (ie. the [0] of each listener array), does some processing then broadcasts to the rest.
	CreateListeners() {
		// add root key-listeners
		this.keyDownListeners = [e=>this.keyDownListeners.slice(1).forEach(listener=>listener(e))];
		document.addEventListener("keydown", this.keyDownListeners[0] as any);
		this.keyUpListeners = [e=>this.keyUpListeners.slice(1).forEach(listener=>listener(e))];
		document.addEventListener("keyup", this.keyUpListeners[0] as any);

		// add root mouse-listeners
		this.mouseDownListeners = [e=>{
			this.recentPointerPositions.set(Date.now(), new Vec2(e.pageX, e.pageY));

			this.mouseDownListeners.slice(1).forEach(listener=>listener(e));
		}];
		document.addEventListener("pointerdown", this.mouseDownListeners[0] as any);
		this.mouseUpListeners = [e=>{
			if (this.c.general.mouseMoveRepeatReject_enabled) {
				this.currentPointerInput_processedPositions.clear();
			}

			this.mouseUpListeners.slice(1).forEach(listener=>listener(e));
		}];
		document.addEventListener("pointerup", this.mouseUpListeners[0] as any);
		this.mouseMoveListeners = [e=>{
			const currentPos = new Vec2(e.pageX, e.pageY);
			this.recentPointerPositions.set(Date.now(), currentPos);
			// mouse-move min-dist-to-trigger filter
			if (this.UsePointerMoveWindow) {
				const windowStart = Date.now() - this.c.general.mouseMove_compareWindow;
				const recentPointerPositionEntries = [...this.recentPointerPositions.entries()].OrderBy(([time, pos])=>time); // they should already be sorted by time, but use OrderBy to make sure
				const posAtStartOfWindow = recentPointerPositionEntries.filter(([time, pos])=>time <= windowStart).LastOrX()?.[1] ?? recentPointerPositionEntries[0]?.[1]; // if window-start is before first entry, use first entry
				const dist = currentPos.DistanceTo(posAtStartOfWindow);
				if (dist < this.c.general.mouseMove_minDistToTrigger) return;
			}
			// mouse-move repeat-reject filter
			if (this.c.general.mouseMoveRepeatReject_enabled) {
				const posKey = `${e.pageX.RoundTo_Str(this.c.general.mouseMoveRepeatReject_roundPositionsTo)},${e.pageY.RoundTo_Str(this.c.general.mouseMoveRepeatReject_roundPositionsTo)}`;
				if (this.currentPointerInput_processedPositions.has(posKey)) return;
				this.currentPointerInput_processedPositions.add(posKey);
			}

			this.mouseMoveListeners.slice(1).forEach(listener=>listener(e));
		}];
		document.addEventListener("pointermove", this.mouseMoveListeners[0] as any);

		// test
		/*document.addEventListener("pointerdown", ()=>(console.log("pointerdown"), (this.mouseMoveListeners[0] as any)()));
		document.addEventListener("pointermove", ()=>(console.log("pointermove"), (this.mouseMoveListeners[0] as any)()));
		document.addEventListener("pointerup", ()=>(console.log("pointerup"), (this.mouseMoveListeners[0] as any)()));
		document.addEventListener("touchstart", ()=>(console.log("touchstart"), (this.mouseMoveListeners[0] as any)())); 
		document.addEventListener("touchend", ()=>(console.log("touchend"), (this.mouseMoveListeners[0] as any)()));*/

		// define root mic-listener
		this.micLoudnessListeners = [(notifyLoudness, actualLoudness)=>{
			//console.log(`NotifyLoudness: ${message["notifyLoudness"]}; ActualLoudness: ${message["actualLoudness"]}`);
			// only snooze prompting; don't snooze_fail, since that might trigger sound-effect, causing issues due to frequency (todo: add min-interval option to avoid this workaround)
			//this.Snooze(`Snoozing (mic loudness: ${message["actualLoudness"].toFixed(4)})`, false); // don't apply external-effects (eg. sound effect) if already applied, since can cause issues due to frequency
			this.micLoudnessListeners.slice(1).forEach(listener=>listener(notifyLoudness, actualLoudness));
		}];
		// if needs mic-listener, and not in android, set up web mic-listener (when in android, mic-listener is initialized through the general Init function)
		if (this.NeedsMicListener && !InAndroid(0)) {
			/*await*/ this.CreateMicListener_Web();
		}

		// add root screen-state-change listener
		this.screenStateChangeListeners = [screenState=>{
			this.screenStateChangeListeners.slice(1).forEach(listener=>listener(screenState));
		}];

		// add root phase-enter listener (the index-0 listener is called by AlarmsComp.SetPhase)
		this.phaseEnterListeners = [newPhase=>{
			this.phaseEnterListeners.slice(1).forEach(listener=>listener(newPhase));
		}];

		// add root phone-motion listener
		this.phoneMotionDataListeners = [motionAveragesOfChunksFromLast10s=>{
			this.phoneMotionDataListeners.slice(1).forEach(listener=>listener(motionAveragesOfChunksFromLast10s));
		}];

		// add root long-inhale listener
		this.longInhaleListeners = [timeOfSignalCompleted=>{
			this.longInhaleListeners.slice(1).forEach(listener=>listener(timeOfSignalCompleted));
		}];

		// Why are we registering these "root listeners" (at index 0) in the arrays above, but then not registering anything here to call them?
		// Well, those index-0 listeners are generally called by event-handlers registered elsewhere (mostly in the files under Utils/Services, eg. PhoneSensors.ts).
		// Some of these event-handlers registered elsewhere hold state in the frontend, so the calls below are to clear that data (since may be left-over from a previous session / period where that pipeline was active).
		ResetPhoneMotionAveragesBuffer();
	}

	// helpers for setting up mic-listeners
	get SequencesWithMicListeners() {
		return EngineConfig.GetAllTriggerSequences(this.c).filter(sequence=>sequence.items.find(a=>a.micLoudness_loudness));
	}
	get NeedsMicListener() {
		return this.SequencesWithMicListeners.length >= 1;
	}
	get MicNotifyLoudnesses() {
		return this.SequencesWithMicListeners.SelectMany(sequence=>{
			return sequence.items.filter(a=>a.micLoudness_loudness).map(a=>a.micLoudness_loudness);
		}).Distinct();
	}
	async CreateMicListener_Web() {
		await this.micRecorder.StartRecording_Audio(store.main.settings.audio.mainMicrophoneID);

		this.micRecorder_audioContext = new AudioContext();
		await InitAudioNodes(this.micRecorder_audioContext);
		const retrieveAudioProcessorNode = CreateGeneralAudioProcessor(this.micRecorder_audioContext);
		retrieveAudioProcessorNode.port.onmessage = event=>{
			const message = event.data;
			if (!this.running) {
				LogWarning("Received message from audio-processor, but session already closed! Message: ", message);
				return;
			}
			if (message.type == "notify-loudness") {
				this.micLoudnessListeners[0](message["notifyLoudness"], message["actualLoudness"]);
			}
		};
		retrieveAudioProcessorNode.port.postMessage({
			type: "init",
			//notifyLoudnesses: [this.c.snoozeFailTrigger_micLoudness_loudness],
			notifyLoudnesses: this.MicNotifyLoudnesses,
			minNotifyInterval: 1,
		});
		//retrieveAudioProcessorNode.port.start();

		const recorderStream = this.micRecorder_audioContext.createMediaStreamSource(this.micRecorder.stream);
		recorderStream.connect(retrieveAudioProcessorNode);
		retrieveAudioProcessorNode.connect(this.micRecorder_audioContext.destination);
		console.log("Microphone listener initialized.");
	}

	// helpers for setting up phone-motion listeners
	get SequencesWithPhoneMotionListeners() {
		return EngineConfig.GetAllTriggerSequences(this.c).filter(sequence=>sequence.items.find(a=>a.checkWindow != null));
	}
	get NeedsPhoneMotionListener() {
		return this.SequencesWithPhoneMotionListeners.length >= 1;
	}

	RemoveListeners() {
		// todo: maybe rather than adding/removing these root handlers, instead create them all ahead of time (eg. in WebSensors.ts file), and just pass the data on to the live-session (if one is active)
		document.removeEventListener("keydown", this.keyDownListeners[0] as any);
		document.removeEventListener("keyup", this.keyUpListeners[0] as any);
		document.removeEventListener("pointerdown", this.mouseDownListeners[0] as any);
		document.removeEventListener("pointerup", this.mouseUpListeners[0] as any);
		document.removeEventListener("pointermove", this.mouseMoveListeners[0] as any);
		document.removeEventListener("touchmove", this.mouseMoveListeners[0] as any);

		this.DestroyMicListener();
	}
	async DestroyMicListener() {
		// stop microphone system
		if (this.micRecorder) await this.micRecorder.StopRecording();
		if (this.micRecorder_audioContext) await this.micRecorder_audioContext.close();
	}

	InitAndroid() {
		//const snoozeTriggerSet = State(a=>a.main.engine.config.remoteConfig).snoozeAndPrompts.snooze_triggerSet;
		const sessionHelpersOptions = E(
			{
				// global settings
				voiceOnRingtoneChannel: store.main.settings.audio.voiceOnRingtoneChannel,
				autoRestart: store.main.settings.autoRestartService,
				sessionCrashCheck_enabled: this.c.general.sessionCrashCheck_enabled,
				sessionCrashCheck_interval: this.c.general.sessionCrashCheck_interval,
				sessionJSWake_enabled: store.main.settings.sessionJSWake_enabled,
				sessionJSWake_interval: store.main.settings.sessionJSWake_interval,
				sessions_systemStartVolume_media_enabled: store.main.settings.audio.sessions_systemStartVolume_media_enabled,
				sessions_systemStartVolume_media: store.main.settings.audio.sessions_systemStartVolume_media,
				sessions_systemStartVolume_call_enabled: store.main.settings.audio.sessions_systemStartVolume_call_enabled,
				sessions_systemStartVolume_call: store.main.settings.audio.sessions_systemStartVolume_call,
				sessions_systemStartVolume_ringtone_enabled: store.main.settings.audio.sessions_systemStartVolume_ringtone_enabled,
				sessions_systemStartVolume_ringtone: store.main.settings.audio.sessions_systemStartVolume_ringtone,

				// panel settings
				//engineContext: EngineContext[State(a=>a.main.engine.config.engineContext)],
				/*snoozeFailTrigger_volumeKeys: State(a=>a.main.engine.config.remote_snoozeFailTrigger_volumeKeys),
				snoozeFailTrigger_micLoudness: State(a=>a.main.engine.config.remote_snoozeFailTrigger_micLoudness),*/
				volumeKeyListener_notify: EngineConfig.GetAllTriggerSequenceItems(this.c).Any(item=>["VolumeUp", "VolumeDown"].includes(item.key_name)),
				micListener_notifyLoudnesses: this.MicNotifyLoudnesses,
				micListener_minNotifyInterval: 1,
				//micListener_logLoudnesses: true,
				phoneMotionListenerNeeded: this.NeedsPhoneMotionListener,
			},
		);
		PluginGeneral.StartSessionHelpers(sessionHelpersOptions);
	}

	//triggerActions: TriggerAction[] = [];
	/*triggerActions = {} as {[key: string]: TriggerAction};
	CreateTriggerAction(name: string, action: TriggerAction) {
		this.triggerActions[name] = action;
	}*/

	triggerPackages = [] as TriggerPackage[];
	RegisterTriggerPackages() {
		// don't worry; the trigger-actions below are only actually called if this is a local session
		const triggerPackages = [] as TriggerPackage[];
		//triggerPackages.push(...this.GetRootTriggerPackages());
		this.components.forEach(comp=>{
			triggerPackages.push(...comp.GetTriggerPackages());
		});
		for (const pack of triggerPackages) {
			this.RegisterTriggerPackage(pack);
		}
	}
	RegisterTriggerPackage(pack: TriggerPackage) {
		/*if (this.IsRemote()) {
			const self = this;
			pack.actionIfLocal = triggerInfo=>{
				this.Log(`Running trigger-action on host: ${pack.name} TriggerInfo: ${ToJSON(triggerInfo)}`, LogType.Action);
				self.hostBridge.Call("RunTriggerAction", pack.name, triggerInfo);
			};
			//pack.enabled = true; // all trigger-packages are enabled for remote sessions, since they may be enabled for the host session
		}*/
		if (this.IsClient() && pack.actionIfRemote == null) {
			pack.actionIfRemote = triggerInfo=>{
				this.RunTriggerActionOnHost(pack.name, triggerInfo);
			};
		}
		this.triggerPackages.push(pack);

		// also register the trigger-package on the individual component (so on suspend/unsuspend, it can reset the activators' states)
		pack.comp.triggerPackages.push(pack);
	}

	//triggerActivators = [] as TriggerSet_Activator[];
	SetUpTriggerActivators() {
		for (const pack of this.triggerPackages) {
			// For a trigger to even have its trigger-activator created initially:
			// * Comp must be enabled at session start (ie. in config), AND...
			// * The trigger itself must be defined, AND...
			// * The trigger itself must not be disabled
			if (!pack.comp.triggersEnabled || pack.condition == null || pack.condition?.disabled) continue;

			const action = this.IsLocal() ? pack.actionIfLocal : pack.actionIfRemote;
			pack.activator = new TriggerSet_Activator(pack.name, pack.condition, action, this);
		}
		//const packagesWithActivators = this.triggerPackages.filter(a=>a.activator);

		const SendInputToActivators = (transferFunc: (activator: TriggerSet_Activator, preState: {inputTime: number, phase: Phase})=>any)=>{
			// Acquire the input-time + phase here, so that all activators receive the same state.
			// * Consistent timestamps: so that eg. competing key-tap and key-hold triggers for a key do not both fire.
			// * Consistent phase: so eg. one trigger that changes the phase doesn't affect what other triggers fire.
			const preState = {
				inputTime: Date.now(),
				phase: this.Comp(AlarmsComp).phase,
			};

			// as in window.addEventListener("WakeUp", [...]), trigger any overdue timers here (the wake-up timer doesn't always activate fast enough, and we need precision for input events)
			if (InAndroid(0) && store.main.settings.sessionJSWake_enabled) {
				this.timerContext.ManuallyTriggerOverdueTimers(1000); // QUESTION: Is 1-second a good threshold?
			}

			// use a two-step process for sending this input-event to the activators
			// this way, the (arbitrary) call order can't change which activators receive it (due to comp suspends/unsuspends)
			// (for example, if user triggered a "switch mode" hotkey, only the old mode/component should also be able to process that input-event)
			const activatorsToSendTo = this.triggerPackages.filter(pack=>{
				if (pack.activator == null) return false;
				// we only need to check comp.triggersEnabled here, because failure on the other checks would have left pack.activator as null
				if (!pack.comp.triggersEnabled) return false;
				return true;
			}).map(a=>a.activator!);
			for (const activator of activatorsToSendTo) {
				transferFunc(activator, preState);
			}
		};
		this.keyDownListeners.push(e=>SendInputToActivators((a, s)=>a.onKeyDown(e, s)));
		this.keyUpListeners.push(e=>SendInputToActivators((a, s)=>a.onKeyUp(e, s)));
		this.mouseDownListeners.push(e=>SendInputToActivators((a, s)=>a.onMouseDown(e, s)));
		this.mouseUpListeners.push(e=>SendInputToActivators((a, s)=>a.onMouseUp(e, s)));
		this.mouseMoveListeners.push(e=>SendInputToActivators((a, s)=>a.onMouseMove(e, s)));
		this.micLoudnessListeners.push((notifyLoudness, actualLoudness)=>SendInputToActivators((a, s)=>a.onMicLoudness(notifyLoudness, actualLoudness, s)));
		this.screenStateChangeListeners.push(screenState=>SendInputToActivators((a, s)=>a.onScreenStateChange(screenState, s)));
		this.phaseEnterListeners.push(newPhase=>SendInputToActivators((a, s)=>a.onPhaseEnter(newPhase, s)));
		this.phoneMotionDataListeners.push(motionAveragesOfChunksFromLast10s=>SendInputToActivators((a, s)=>a.onPhoneMotionData(motionAveragesOfChunksFromLast10s, s)));
		this.longInhaleListeners.push(timeOfSignalCompleted=>SendInputToActivators((a, s)=>a.onLongInhale(timeOfSignalCompleted, s)));
	}

	async PlaySoundEffect(soundTag: string, volume = 1, loop = false, tryPlayMissingSound = false) {
		this.effectSoundPlayer.sound = GetSounds_WithUserTag(soundTag).Random();
		if (this.effectSoundPlayer.youtubePlayer) {
			this.effectSoundPlayer.youtubePlayer.loop = loop;
		}
		if (this.effectSoundPlayer.sound || tryPlayMissingSound) {
			await this.effectSoundPlayer.Play(volume);
		}
	}

	logEntries = [] as LogEntry[];
	Log(message: string, type = LogType.Event_Small, opt?: LogOptions_ForGroupX) {
		return SessionLog(message, type, E({group: this}, opt));
	}
	AddLogEntry(entry: LogEntry, alsoLogToConsole: boolean|n = null) {
		GroupX_AddSessionLogEntry(this, entry, alsoLogToConsole);
	}
	ClearLog() {
		this.logEntries.Clear();
		NotifyLiveSessionLogChanged();
	}
}